import Email from "@/domain/entities/emails/Email";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import FeatureFlag from "@/domain/enums/FeatureFlag";
import MailActionType from "@/domain/enums/MailActionType";
import { Tone } from "@/hooks/useGenerateAnswerWithIA";
import { isSameCopy } from "@/lib/mailIdentity";
import deepFocusStore, { setDeepFocusActive } from "@/stores/deepFocusStore";
import mailStore, { setOpenedMail } from "@/stores/mailStore";
import { useFeatureFlag } from "@cs_teamwaydev/mailvista-sdk_react";
import { useSelector } from "@tanstack/react-store";
import { Editor } from "@tiptap/react";
import { Dispatch, RefObject, SetStateAction, useEffect, useRef } from "react";
import { FocusTarget } from "../mailsList/MailAction";
import { TooltipProvider } from "../ui/tooltip";
import DeepFocusEntryButton from "./DeepFocusEntryButton";
import DeepFocusOverlay from "./DeepFocusOverlay";

interface DeepFocusSelectProps {
    mails: Array<Email>;
    loadNextPage: () => void;
    isLoading?: boolean;
    sideListMode?: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    editor: Editor;
    mailInputExpanded: boolean;
    setMailInputExpanded: (
        inputExpanded: boolean,
        focusTarget?: FocusTarget,
    ) => void;
    toInputRef: RefObject<HTMLInputElement | null>;
    attachments: EmailAttachment[];
    setAttachments: Dispatch<SetStateAction<EmailAttachment[]>>;
    acceptOrRefuseSuggestion: (accept: boolean) => void;
    isLoadingSuggestion: boolean;
    isPendingChange: boolean;
    makeSuggestion: (action: Tone) => void;
    pendingSuggestion: boolean;
    setPendingSuggestion: Dispatch<SetStateAction<boolean>>;
    suggestionEditor: Editor;
}

const DeepFocusSelect = ({
    mails,
    loadNextPage,
    isLoading,
    sideListMode,
    hasNextPage,
    isFetchingNextPage,
    editor,
    mailInputExpanded,
    setMailInputExpanded,
    toInputRef,
    attachments,
    setAttachments,
    acceptOrRefuseSuggestion,
    isLoadingSuggestion,
    isPendingChange,
    makeSuggestion,
    pendingSuggestion,
    setPendingSuggestion,
    suggestionEditor,
}: DeepFocusSelectProps) => {
    const isMailDeepFocusEnabled = useFeatureFlag(
        FeatureFlag.MAIL_ENABLE_DEEPFOCUS,
    );
    const isDeepFocusActive = useSelector(
        deepFocusStore,
        (state) => state.isActive,
    );

    // Laissé ouvert en vue liste, le dernier mail du Deep Focus empêchait les actions de réponse d'ouvrir le volet
    const openedMailBeforeDeepFocus = useRef<{
        mail: Email | undefined;
        actionType: MailActionType;
    } | null>(null);

    // Le nettoyage au démontage ne voit que les props du premier rendu
    const liveMailsRef = useRef(mails);
    useEffect(() => {
        liveMailsRef.current = mails;
    });

    const leaveDeepFocus = () => {
        setDeepFocusActive(false);
        if (!openedMailBeforeDeepFocus.current) return;

        const { mail, actionType } = openedMailBeforeDeepFocus.current;
        openedMailBeforeDeepFocus.current = null;
        // Dernier mail traité : la liste lue ici peut encore le contenir si elle s'est vidée au même rendu
        if (!mail || !mailStore.state.openedMail) {
            setOpenedMail(undefined, { openIntent: false });

            return;
        }

        const liveMails = liveMailsRef.current;
        const findLiveCopy = (wanted: Email | undefined) =>
            wanted && liveMails.find((live) => isSameCopy(live, wanted));
        const mailBeforeDeepFocus = findLiveCopy(mail);
        if (mailBeforeDeepFocus) {
            setOpenedMail(mailBeforeDeepFocus, {
                actionType,
                openIntent: false,
            });

            return;
        }

        setOpenedMail(
            findLiveCopy(mailStore.state.openedMail) ?? liveMails[0],
            { openIntent: false },
        );
    };

    // Fenêtre trop étroite ou changement de liste
    useEffect(
        () => () => {
            if (deepFocusStore.state.isActive) {
                leaveDeepFocus();
            } else {
                setDeepFocusActive(false);
            }
        },
        [],
    );

    const handleEnterDeepFocus = () => {
        openedMailBeforeDeepFocus.current = {
            mail: mailStore.state.openedMail,
            actionType: mailStore.state.mailActionType,
        };
        setDeepFocusActive(true);
        setOpenedMail(mails[0], {
            actionType: MailActionType.REPLY,
            openIntent: false,
        });
    };

    const handleExitDeepFocus = () => {
        leaveDeepFocus();
    };

    return (
        <div className="relative z-modal-panel">
            <DeepFocusOverlay
                acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                attachments={attachments}
                editor={editor}
                hasNextPage={hasNextPage}
                isDeepFocusActive={isDeepFocusActive}
                isFetchingNextPage={isFetchingNextPage}
                isLoading={isLoading}
                isLoadingSuggestion={isLoadingSuggestion}
                isPendingChange={isPendingChange}
                loadNextPage={loadNextPage}
                mailInputExpanded={mailInputExpanded}
                mails={mails}
                makeSuggestion={makeSuggestion}
                onClose={handleExitDeepFocus}
                pendingSuggestion={pendingSuggestion}
                setAttachments={setAttachments}
                setMailInputExpanded={setMailInputExpanded}
                setPendingSuggestion={setPendingSuggestion}
                suggestionEditor={suggestionEditor}
                toInputRef={toInputRef}
            />

            <div className="absolute right-6 top-0 z-modal-backdrop flex h-15 items-center justify-center">
                <TooltipProvider delayDuration={0}>
                    <DeepFocusEntryButton
                        disabled={!isMailDeepFocusEnabled}
                        onClick={handleEnterDeepFocus}
                        showNotInPlanTooltip={!isMailDeepFocusEnabled}
                        sideListMode={sideListMode}
                    />
                </TooltipProvider>
            </div>
        </div>
    );
};

export default DeepFocusSelect;
